/* ------------------------------------------------------------------ */
/* Seção 2 — Sistemas Processuais (agrupado por sistema)                */
/* ------------------------------------------------------------------ */
import { useState } from 'react';
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { NivelBadge, StatusProAdvBadge, type NivelAcesso, type StatusProAdv } from './badges';

interface TribunalChip {
  sigla: string;
  nome: string;
  statusMigracao?: 'ativo' | 'em-migracao' | 'legado' | 'piloto';
  statusProAdv: StatusProAdv;
  obs?: string;
}

interface TipoIntegracao {
  tipo: string;
  disponivel: boolean;
  obs?: string;
}

interface SistemaProcessual {
  id: string;
  nome: string;
  desenvolvedor: string;
  nivelAcesso: NivelAcesso;
  miniDescricao: string;
  descricao: string;
  tribunais: TribunalChip[];
  oQueGanho: string[];
  usoPratico: string[];
  tiposIntegracao: TipoIntegracao[];
  requisitosObrigatorios: string[];
  requisitosOpcionais: string[];
  estrategia: string;
  fontes?: Array<{ label: string; url: string }>;
}

const migracaoConfig: Record<
  NonNullable<TribunalChip['statusMigracao']>,
  { label: string; bg: string; text: string }
> = {
  ativo: { label: 'Ativo', bg: 'bg-green-500/15', text: 'text-green-400' },
  'em-migracao': { label: 'Em migração', bg: 'bg-yellow-500/15', text: 'text-yellow-400' },
  legado: { label: 'Legado', bg: 'bg-zinc-500/15', text: 'text-zinc-400' },
  piloto: { label: 'Piloto VoxisAdv', bg: 'bg-blue-500/15', text: 'text-blue-400' },
};

const sistemasProcessuais: SistemaProcessual[] = [
  {
    id: 'eproc',
    nome: 'eProc',
    desenvolvedor: 'TRF4 — Tribunal Regional Federal da 4ª Região',
    nivelAcesso: 'so-scraping',
    miniDescricao: 'Sistema do tribunal piloto (TJRS). Padrão nacional emergente — TJSP migrando desde out/2025.',
    descricao:
      'Desenvolvido internamente pelo TRF4 e cedido gratuitamente a outros tribunais por cooperação técnica. É o sistema do TJRS (cliente piloto do ProAdv). O movimento nacional é pró-eProc: TJSP iniciou migração do eSAJ em out/2025 (prazo de até 5 anos), TJMG aderiu saindo do PJe. TJRS, TJSC, TJTO, TJAC, TRF2, TRF4, TRF6 já operam em eProc. Não há API REST pública nem webservice MNI para sistemas privados — o único caminho é scraping autenticado. Para consultar processos, login/senha do advogado + 2FA TOTP é suficiente (validado pelo ADVBOX com a usuária piloto Bárbara, sem certificado). O certificado A1 é opcional: método alternativo de login para quem prefere, e pré-requisito para peticionar/assinar eletronicamente (fase além do MVP de consulta).',
    tribunais: [
      { sigla: 'TJRS', nome: 'Tribunal de Justiça do RS', statusMigracao: 'piloto', statusProAdv: 'planejado', obs: 'Bárbara OAB-RS 087113 — J05 alvo #1' },
      { sigla: 'TRF4', nome: 'TRF 4ª Região (RS/SC/PR)', statusMigracao: 'ativo', statusProAdv: 'planejado', obs: 'Originador do eProc; base para adapter TJRS' },
      { sigla: 'TRF2', nome: 'TRF 2ª Região (RJ/ES)', statusMigracao: 'ativo', statusProAdv: 'sem-plano' },
      { sigla: 'TRF6', nome: 'TRF 6ª Região (MG)', statusMigracao: 'ativo', statusProAdv: 'sem-plano' },
      { sigla: 'TJSC', nome: 'Tribunal de Justiça de SC', statusMigracao: 'ativo', statusProAdv: 'sem-plano', obs: 'Migrou do SAJ' },
      { sigla: 'TJMG', nome: 'Tribunal de Justiça de MG', statusMigracao: 'ativo', statusProAdv: 'sem-plano', obs: 'Migrou do PJe' },
      { sigla: 'TJRJ', nome: 'Tribunal de Justiça do RJ', statusMigracao: 'ativo', statusProAdv: 'sem-plano' },
      { sigla: 'TJTO', nome: 'Tribunal de Justiça do TO', statusMigracao: 'ativo', statusProAdv: 'sem-plano' },
      { sigla: 'TJAC', nome: 'Tribunal de Justiça do AC', statusMigracao: 'ativo', statusProAdv: 'sem-plano' },
      { sigla: 'TJSP', nome: 'Tribunal de Justiça de SP', statusMigracao: 'em-migracao', statusProAdv: 'sem-plano', obs: 'Migração SAJ→eProc iniciada out/2025, prazo de até 5 anos' },
      { sigla: 'TJAL', nome: 'Tribunal de Justiça de AL', statusMigracao: 'ativo', statusProAdv: 'sem-plano' },
      { sigla: 'TJBA', nome: 'Tribunal de Justiça da BA', statusMigracao: 'ativo', statusProAdv: 'sem-plano' },
      { sigla: 'TJMS', nome: 'Tribunal de Justiça do MS', statusMigracao: 'ativo', statusProAdv: 'sem-plano' },
      { sigla: 'TJSE', nome: 'Tribunal de Justiça do SE', statusMigracao: 'em-migracao', statusProAdv: 'sem-plano', obs: 'Em implantação' },
      { sigla: 'STM', nome: 'Superior Tribunal Militar', statusMigracao: 'ativo', statusProAdv: 'sem-plano' },
      { sigla: 'TJMRS', nome: 'Tribunal de Justiça Militar do RS', statusMigracao: 'ativo', statusProAdv: 'sem-plano' },
      { sigla: 'TJMMG', nome: 'Tribunal de Justiça Militar de MG', statusMigracao: 'ativo', statusProAdv: 'sem-plano' },
    ],
    oQueGanho: [
      'Acompanhamento em tempo quasi-real dos autos do processo',
      'Movimentações, decisões e documentos dos autos',
      'Intimações pendentes de ciência pelo advogado',
      'Cobertura crescente: cada novo tribunal que adere ao eProc vira custo marginal zero',
    ],
    usoPratico: [
      'Processos novos descobertos automaticamente por OAB — criados/atualizados em D02 sem ação manual',
      'Aba "Andamentos" do processo — movimentações completas com inteiro teor, badge "via eProc TJRS"',
      'Aba "Peças / Documentos" — PDFs baixados e armazenados em F06, vinculados ao processo via D07',
      'Botão "Sincronizar agora" na tela do processo para atualização sob demanda',
      'Integração bloqueada atualmente (depende de cert em nuvem da Bárbara + VPS brasileira)',
    ],
    tiposIntegracao: [
      { tipo: 'RPA / browser automation com login do advogado', disponivel: true, obs: 'Login/senha cobre 100% do scraping — A1 é alternativa equivalente, não exige ambos. Ambos passam pelo 2FA TOTP.' },
      { tipo: 'API REST pública para SaaS privado', disponivel: false, obs: 'Não existe — eProc não disponibiliza para terceiros privados' },
      { tipo: 'MNI/SOAP (webservice oficial)', disponivel: false, obs: 'Restrito a órgãos do Judiciário — não disponível para escritórios' },
      { tipo: 'DataJud (complemento de metadados)', disponivel: true, obs: 'Via J02 para capa processual sem autenticação' },
    ],
    requisitosObrigatorios: [
      'Credenciais do advogado: UF + número OAB, senha e seed TOTP do 2FA',
      'VPS brasileira com ASN BR (Locaweb, KingHost ou Magalu Cloud) — essencial para evitar bloqueio Cloudflare',
      '2FA TOTP obrigatório desde abr/2025 (OAB-RS) — "dispositivo confiável" do eProc dispensa re-validação em browser persistente',
    ],
    requisitosOpcionais: [
      'Certificado A1 do advogado (J04 armazena cifrado AES-256-GCM)',
      '  → Para consulta/scraping: opcional. Login/senha cobre 100% (validado no ADVBOX — Bárbara opera sem certificado). A1 é método alternativo de login, não exigido junto.',
      '  → Para assinar petições/peças (fase futura, não MVP): obrigatório no momento da geração da assinatura, independente de como o advogado logou. Documentação oficial: "O certificado digital só é necessário no momento da geração da assinatura." Sem alternativa: apenas certs ICP-Brasil são aceitos.',
      '  → Cert NÃO dispensa o 2FA — mesmo logando com A1, o TOTP continua sendo exigido.',
      '  → No cadastro inicial do advogado no eProc, pode ser solicitado cert uma vez para identificação; depois disso, login/senha basta no dia a dia.',
    ],
    estrategia:
      'Scraping autenticado por login/senha do advogado + 2FA TOTP. Validado pelo ADVBOX: usuária piloto (Bárbara) opera sem certificado. J05 implementará o adapter que autentica no portal eProc e extrai movimentações; suportará ambos os métodos de auth (senha ou cert). Primeiro alvo: TJRS (cliente piloto). Um adapter cobre todos os tribunais eProc com ajustes mínimos de URL.',
    fontes: [
      { label: 'Portal eProc — TRF4', url: 'https://www.trf4.jus.br/eproc' },
      { label: 'TJSP adere ao eProc (ago/2024)', url: 'https://www.conjur.com.br/2024-ago-21/adesao-do-tjsp-a-novo-sistema-processual-trara-beneficios-a-justica-paulista-diz-barroso/' },
      { label: 'ulisses-jurisdev/datajud-process-scraper (arquitetura híbrida DataJud + Selenium)', url: 'https://github.com/ulisses-jurisdev/datajud-process-scraper' },
      { label: 'joaotextor/busca-processos-judiciais (DataJud como base)', url: 'https://github.com/joaotextor/busca-processos-judiciais' },
    ],
  },
  {
    id: 'pje',
    nome: 'PJe',
    desenvolvedor: 'CNJ — Conselho Nacional de Justiça',
    nivelAcesso: 'mni-restrito',
    miniDescricao: 'Sistema do CNJ. Cobre todos os TRTs (Trabalhista), TST e vários TRFs/TJs estaduais.',
    descricao:
      'Criado pelo CNJ com o objetivo de padronizar o processo eletrônico. É o mais difundido em volume: cobre todos os TRTs, TST, TRF1, TRF3, TRF5 e vários TJs estaduais. Possui webservice MNI (Modelo Nacional de Interoperabilidade) baseado em SOAP, mas o acesso é restrito a órgãos do Judiciário e entidades públicas credenciadas (MP, Defensoria, outros tribunais) — escritórios privados não têm esse canal oficial. O movimento nacional de migração de TJs para eProc não afeta os TRTs, que devem seguir no PJe por tempo indeterminado.',
    tribunais: [
      { sigla: 'TST', nome: 'Tribunal Superior do Trabalho', statusMigracao: 'ativo', statusProAdv: 'sem-plano' },
      { sigla: 'TRT4', nome: 'TRT 4ª Região (RS)', statusMigracao: 'ativo', statusProAdv: 'sem-plano', obs: 'Relevante para advogados trabalhistas do RS' },
      { sigla: 'TRT2', nome: 'TRT 2ª Região (SP)', statusMigracao: 'ativo', statusProAdv: 'sem-plano', obs: 'Maior TRT do país em volume' },
      { sigla: 'TRT1', nome: 'TRT 1ª Região (RJ)', statusMigracao: 'ativo', statusProAdv: 'sem-plano' },
      { sigla: 'TRT3', nome: 'TRT 3ª Região (MG)', statusMigracao: 'ativo', statusProAdv: 'sem-plano' },
      { sigla: 'TRT5', nome: 'TRT 5ª Região (BA)', statusMigracao: 'ativo', statusProAdv: 'sem-plano' },
      { sigla: 'TRT9', nome: 'TRT 9ª Região (PR)', statusMigracao: 'ativo', statusProAdv: 'sem-plano' },
      { sigla: 'TRT15', nome: 'TRT 15ª Região (Campinas/SP)', statusMigracao: 'ativo', statusProAdv: 'sem-plano' },
      { sigla: 'TRF1', nome: 'TRF 1ª Região (DF e Norte/Centro-Oeste)', statusMigracao: 'ativo', statusProAdv: 'sem-plano', obs: 'Federal — MNI disponível mas restrito' },
      { sigla: 'TRF3', nome: 'TRF 3ª Região (SP/MS)', statusMigracao: 'ativo', statusProAdv: 'sem-plano', obs: 'Federal — mesmo perfil do TRF1' },
      { sigla: 'TRF5', nome: 'TRF 5ª Região (NE)', statusMigracao: 'ativo', statusProAdv: 'sem-plano', obs: 'Federal — Nordeste; MNI restrito' },
      { sigla: 'TJAM', nome: 'Tribunal de Justiça do AM', statusMigracao: 'ativo', statusProAdv: 'sem-plano' },
      { sigla: 'TJMA', nome: 'Tribunal de Justiça do MA', statusMigracao: 'ativo', statusProAdv: 'sem-plano' },
      { sigla: 'TJPI', nome: 'Tribunal de Justiça do PI', statusMigracao: 'ativo', statusProAdv: 'sem-plano' },
      { sigla: 'TJPE', nome: 'Tribunal de Justiça de PE', statusMigracao: 'ativo', statusProAdv: 'sem-plano' },
      { sigla: 'TJPB', nome: 'Tribunal de Justiça da PB', statusMigracao: 'ativo', statusProAdv: 'sem-plano' },
      { sigla: 'TJPA', nome: 'Tribunal de Justiça do PA', statusMigracao: 'ativo', statusProAdv: 'sem-plano' },
      { sigla: 'TJGO', nome: 'Tribunal de Justiça de GO', statusMigracao: 'ativo', statusProAdv: 'sem-plano' },
      { sigla: 'TJMT', nome: 'Tribunal de Justiça do MT', statusMigracao: 'ativo', statusProAdv: 'sem-plano' },
      { sigla: 'TJRO', nome: 'Tribunal de Justiça de RO', statusMigracao: 'ativo', statusProAdv: 'sem-plano' },
    ],
    oQueGanho: [
      'Cobertura de toda a Justiça do Trabalho (todos os 24 TRTs)',
      'Processos federais (TRF1, TRF3, TRF5)',
      'Vários TJs estaduais de menor porte',
    ],
    usoPratico: [
      'Mesmo escopo do eProc: processos descobertos por OAB, andamentos e peças em D02',
      'Aba "Andamentos" com badge "via PJe [tribunal]"',
      'Aba "Peças / Documentos" com PDFs armazenados em F06',
      'Botão "Sincronizar agora" na tela do processo',
      'Sem plano de implementação definido — prioridade baixa enquanto piloto (TJRS/eProc) não estiver estável',
    ],
    tiposIntegracao: [
      { tipo: 'RPA / browser automation com login do advogado', disponivel: true, obs: 'Login/senha é o caminho comum; certificado A1 é alternativo; interface varia por versão do PJe — scraping instável' },
      { tipo: 'API REST pública para SaaS privado', disponivel: false, obs: 'MNI é SOAP/WebService legado, sem padrão REST público' },
      { tipo: 'MNI/SOAP (webservice oficial)', disponivel: false, obs: 'Restrito a órgãos credenciados — escritórios privados não se qualificam' },
      { tipo: 'Escritório Digital CNJ', disponivel: true, obs: 'Voltado ao advogado PF, não a sistemas SaaS — não serve para automação' },
      { tipo: 'DataJud (complemento de metadados)', disponivel: true, obs: 'Via J02 para capa processual sem autenticação' },
    ],
    requisitosObrigatorios: [
      'Credenciais do advogado: UF + número OAB, senha e 2FA (onde exigido pelo tribunal)',
      'Adapter específico por versão do PJe (cada tribunal pode usar versão diferente)',
    ],
    requisitosOpcionais: [
      'Certificado A1 do advogado (ICP-Brasil)',
      '  → Para consulta/scraping: opcional. Login/senha é o caminho comum; A1 é alternativa equivalente — não exige ambos.',
      '  → Para assinar petições/peças (fase futura): obrigatório no momento da assinatura, independente do método de login. Apenas certs ICP-Brasil são aceitos — sem alternativa.',
      '  → Cert NÃO dispensa o 2FA (onde exigido pelo tribunal).',
      'Negociação de credencial MNI tribunal a tribunal (viável apenas para órgãos públicos — fora do escopo).',
    ],
    estrategia:
      'Caminho mais viável para o VoxisAdv: complementar com DataJud (J02) para metadados e aguardar que a Justiça do Trabalho modernize seu acesso. Acesso programático direto ao MNI requer negociação tribunal a tribunal. Scraping instável por variação de versão entre tribunais. Prioridade baixa enquanto a base do piloto (TJRS/eProc) não estiver estável.',
    fontes: [
      { label: 'PJe — CNJ', url: 'https://www.cnj.jus.br/programas-e-acoes/processo-judicial-eletronico-pje/' },
      { label: 'Documentação MNI', url: 'https://docs.pje.jus.br/servicos-auxiliares/servico-mni-client/' },
      { label: 'TRT1 — Acesso automatizado', url: 'https://www.trt1.jus.br/acesso-automatizado-por-sistemas-externos' },
    ],
  },
  {
    id: 'saj',
    nome: 'SAJ / eSAJ',
    desenvolvedor: 'Softplan (empresa privada)',
    nivelAcesso: 'so-scraping',
    miniDescricao: 'Sistema do TJSP (legado). Relevância decrescente — TJSP migrando para eProc desde out/2025.',
    descricao:
      'Sistema comercial da Softplan, historicamente o maior em volume processual por ser o sistema do TJSP. Não possui API pública nem webservice MNI para sistemas privados. O TJSP iniciou migração para eProc em outubro de 2025, com prazo de até 5 anos — durante a transição os dois sistemas coexistem. Outros tribunais que usam SAJ tendem a migrar conforme os contratos com a Softplan vencem.',
    tribunais: [
      { sigla: 'TJSP', nome: 'Tribunal de Justiça de SP', statusMigracao: 'em-migracao', statusProAdv: 'sem-plano', obs: 'Legado SAJ coexiste com eProc novo até ~2030' },
      { sigla: 'TJCE', nome: 'Tribunal de Justiça do CE', statusMigracao: 'ativo', statusProAdv: 'sem-plano' },
      { sigla: 'TJSC', nome: 'Tribunal de Justiça de SC', statusMigracao: 'legado', statusProAdv: 'sem-plano', obs: 'Já migrou para eProc' },
    ],
    oQueGanho: [
      'Andamentos públicos sem autenticação (dados básicos do processo)',
      'Acesso a autos completos com credencial do advogado',
    ],
    usoPratico: [
      'Mesmo escopo do eProc: processos por OAB, andamentos e peças em D02',
      'Aba "Andamentos" com badge "via eSAJ [tribunal]"',
      'Relevância decrescente com migração do TJSP para eProc — processos novos já entram no eProc',
      'Sem plano de implementação no MVP',
    ],
    tiposIntegracao: [
      { tipo: 'RPA / browser automation com login do advogado', disponivel: true, obs: 'eSAJ aceita login/senha (caminho comum) ou certificado A1 (alternativo); ambos suficientes para consulta' },
      { tipo: 'API REST pública para SaaS privado', disponivel: false, obs: 'Não existe — Softplan não disponibiliza' },
      { tipo: 'MNI/SOAP (webservice oficial)', disponivel: false, obs: 'Não existe para o SAJ' },
      { tipo: 'DataJud (complemento de metadados)', disponivel: true, obs: 'Via J02 para capa processual' },
    ],
    requisitosObrigatorios: [
      'Credenciais do advogado no portal eSAJ: usuário/senha',
      'Adapter específico para eSAJ (interface diferente do eProc)',
      'Gestão de coexistência: processos novos do TJSP já entram no eProc, não no SAJ legado',
    ],
    requisitosOpcionais: [
      'Certificado A1 do advogado (ICP-Brasil)',
      '  → Para consulta/scraping: alternativa ao login/senha — não obrigatório para consultar.',
      '  → Para assinar petições/peças (fase futura): obrigatório no momento da assinatura. Apenas certs ICP-Brasil são aceitos — sem alternativa.',
      '  → Cert NÃO dispensa o 2FA.',
    ],
    estrategia:
      'Relevância decrescente com TJSP migrando para eProc. Para o MVP: não é prioridade. Quando aparecer cliente de TJSP com processos no SAJ legado: avaliar scraping no eSAJ ou aguardar migração do processo específico para eProc.',
    fontes: [
      { label: 'eSAJ — TJSP', url: 'https://esaj.tjsp.jus.br/' },
      { label: 'Migração TJSP para eProc (out/2025)', url: 'https://www.tjsp.jus.br/Noticias/Noticia?codigoNoticia=112491' },
    ],
  },
  {
    id: 'projudi',
    nome: 'PROJUDI / Themis / Outros',
    desenvolvedor: 'TJPR (PROJUDI) — TJPE (Themis) — sistemas menores',
    nivelAcesso: 'so-scraping',
    miniDescricao: 'Sistemas legados de menor volume. Fora do escopo do MVP.',
    descricao:
      'Sistemas de processo eletrônico de tribunais menores ou em desuso progressivo. PROJUDI foi desenvolvido pelo TJPR e cedido a alguns tribunais estaduais menores — hoje a maioria migrou para outros sistemas. Themis é o sistema do TJPE. Nenhum possui API pública. Volume residual e sem relevância para o MVP (cliente piloto é TJRS).',
    tribunais: [
      { sigla: 'TJPR', nome: 'Tribunal de Justiça do PR', statusMigracao: 'legado', statusProAdv: 'sem-plano', obs: 'Novas varas em eProc; processos antigos no PROJUDI' },
      { sigla: 'TJRN', nome: 'Tribunal de Justiça do RN', statusMigracao: 'legado', statusProAdv: 'sem-plano', obs: 'Parcial PROJUDI' },
      { sigla: 'TJPE', nome: 'Tribunal de Justiça do PE', statusMigracao: 'ativo', statusProAdv: 'sem-plano', obs: 'Themis — portal próprio' },
    ],
    oQueGanho: [
      'Consulta pública básica de processos sem autenticação',
    ],
    usoPratico: [
      'Mesmo escopo dos demais: andamentos em D02 via scraping público',
      'Sem plano ativo — fora do escopo do MVP',
      'Fallback: DataJud (J02) já cobre metadados básicos desses tribunais sem adapter próprio',
    ],
    tiposIntegracao: [
      { tipo: 'RPA / browser automation (consulta pública)', disponivel: true, obs: 'Dados básicos sem autenticação; acesso a autos requer login/senha do advogado' },
      { tipo: 'API REST pública', disponivel: false, obs: 'Não existe' },
      { tipo: 'MNI/SOAP', disponivel: false, obs: 'Não existe' },
      { tipo: 'DataJud (complemento de metadados)', disponivel: true, obs: 'Via J02 para capa processual' },
    ],
    requisitosObrigatorios: [
      'Credenciais do advogado por tribunal (para acesso a autos além da consulta pública)',
      'Adapter específico por sistema (nenhum compartilha interface com eProc)',
    ],
    requisitosOpcionais: [
      'Certificado A1 (ICP-Brasil)',
      '  → Para consulta/scraping: pode ser aceito como método alternativo em alguns tribunais — não obrigatório para consultar.',
      '  → Para assinar petições/peças (fase futura): obrigatório no momento da assinatura. Apenas certs ICP-Brasil são aceitos — sem alternativa.',
      '  → Cert NÃO dispensa o 2FA onde exigido.',
    ],
    estrategia:
      'Fora do escopo do MVP. Se surgir cliente com processos nesses sistemas: avaliar scraping público + DataJud para metadados, ou aguardar migração do tribunal para eProc.',
    fontes: [
      { label: 'PROJUDI — Consulta pública TJPR', url: 'https://consulta.tjpr.jus.br/projudi_consulta/' },
      { label: 'Themis — TJPE', url: 'https://portal.tjpe.jus.br/servicos/consultas/processos/themis-acompanhamento' },
    ],
  },
];

function TribunalChipComponent({ chip }: { chip: TribunalChip }) {
  const migrCfg = chip.statusMigracao ? migracaoConfig[chip.statusMigracao] : null;
  const proadCfg = chip.statusProAdv;

  return (
    <div
      className="group relative flex flex-col gap-0.5 rounded-md border border-[var(--card-border)] bg-[var(--surface-muted)] px-2 py-1.5 text-center"
      title={[chip.nome, chip.obs].filter(Boolean).join(' — ')}
    >
      <span className="font-mono text-xs font-bold text-[var(--fg)]">{chip.sigla}</span>
      {migrCfg && (
        <span className={`text-[10px] font-medium ${migrCfg.text}`}>{migrCfg.label}</span>
      )}
      <StatusProAdvBadge status={proadCfg} />
    </div>
  );
}

function SistemaCard({ item }: { item: SistemaProcessual }) {
  const [open, setOpen] = useState(false);

  return (
    <Card className="overflow-hidden">
      <button
        type="button"
        className="w-full text-left"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
      >
        <CardHeader className="pb-3">
          <div className="flex items-start justify-between gap-3">
            <div className="flex items-start gap-3">
              <span className="rounded-md bg-[var(--surface-muted)] px-2 py-1 font-mono text-xs font-bold text-[var(--fg-muted)] select-none whitespace-nowrap mt-0.5">
                {item.id.toUpperCase()}
              </span>
              <div>
                <CardTitle className="text-base leading-snug">{item.nome}</CardTitle>
                <p className="mt-0.5 text-xs text-[var(--fg-muted)]">{item.desenvolvedor}</p>
                <p className="mt-1 text-xs text-[var(--fg-muted)] italic">{item.miniDescricao}</p>
              </div>
            </div>
            <div className="flex shrink-0 items-center gap-2">
              <NivelBadge nivel={item.nivelAcesso} />
              <span className="text-[var(--fg-muted)] text-sm select-none">
                {open ? '▲' : '▼'}
              </span>
            </div>
          </div>
        </CardHeader>
      </button>

      {open && (
        <CardContent className="border-t border-[var(--card-border)] pt-4 space-y-5">
          <p className="text-sm text-[var(--fg-muted)] leading-relaxed">{item.descricao}</p>

          {/* Tribunais */}
          <div>
            <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-[var(--fg-muted)]">
              Tribunais que usam este sistema
            </p>
            <div className="flex flex-wrap gap-2">
              {item.tribunais.map((t) => (
                <TribunalChipComponent key={t.sigla} chip={t} />
              ))}
            </div>
            <p className="mt-2 text-[11px] text-[var(--fg-muted)]">
              Passe o mouse sobre o chip para ver nome completo e observação.
            </p>
          </div>

          {/* O que ganho integrando */}
          <div>
            <p className="mb-2 flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wide text-green-400">
              <span>✓</span> O que ganho integrando
            </p>
            <ul className="space-y-1.5">
              {item.oQueGanho.map((txt, i) => (
                <li key={i} className="flex gap-2 text-sm text-[var(--fg)]">
                  <span className="mt-0.5 shrink-0 text-green-400">•</span>
                  {txt}
                </li>
              ))}
            </ul>
          </div>

          {/* Uso prático no app */}
          {item.usoPratico.length > 0 && (
            <div className="rounded-lg border border-blue-500/20 bg-blue-500/5 p-3">
              <p className="mb-2 flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wide text-blue-400">
                <span>◈</span> Uso prático no app
              </p>
              <ul className="space-y-1.5">
                {item.usoPratico.map((txt, i) => (
                  <li key={i} className="flex gap-2 text-sm text-[var(--fg)]">
                    <span className="mt-0.5 shrink-0 text-blue-400">•</span>
                    {txt}
                  </li>
                ))}
              </ul>
            </div>
          )}

          {/* Tipos de integração */}
          <div>
            <p className="mb-2 flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wide text-[var(--fg-muted)]">
              <span>⊞</span> Tipo de integração possível
            </p>
            <ul className="space-y-2">
              {item.tiposIntegracao.map((t, i) => (
                <li key={i} className="flex items-start gap-2 text-sm">
                  <span className={`mt-0.5 shrink-0 font-bold ${t.disponivel ? 'text-green-400' : 'text-red-400'}`}>
                    {t.disponivel ? '✅' : '❌'}
                  </span>
                  <span className={t.disponivel ? 'text-[var(--fg)]' : 'text-[var(--fg-muted)]'}>
                    {t.tipo}
                    {t.obs && <span className="ml-1 text-xs text-[var(--fg-muted)]">— {t.obs}</span>}
                  </span>
                </li>
              ))}
            </ul>
          </div>

          {/* O que preciso pra fazer */}
          <div>
            <p className="mb-3 flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wide text-[var(--fg-muted)]">
              <span>→</span> O que preciso pra fazer
            </p>
            <div className="space-y-3">
              {item.requisitosObrigatorios.length > 0 && (
                <div className="rounded-lg border border-orange-500/20 bg-orange-500/5 p-3">
                  <p className="mb-2 text-[11px] font-semibold uppercase tracking-wide text-orange-400">
                    Obrigatório (para consulta)
                  </p>
                  <ul className="space-y-1.5">
                    {item.requisitosObrigatorios.map((txt, i) => (
                      <li key={i} className="flex gap-2 text-sm text-[var(--fg)]">
                        <span className="mt-0.5 shrink-0 text-orange-400">•</span>
                        {txt}
                      </li>
                    ))}
                  </ul>
                </div>
              )}
              {item.requisitosOpcionais.length > 0 && (
                <div className="rounded-lg border border-zinc-500/20 bg-zinc-500/5 p-3">
                  <p className="mb-2 text-[11px] font-semibold uppercase tracking-wide text-zinc-400">
                    Opcional (alternativa de auth ou para peticionamento futuro)
                  </p>
                  <ul className="space-y-1.5">
                    {item.requisitosOpcionais.map((txt, i) => (
                      <li key={i} className="flex gap-2 text-sm text-[var(--fg-muted)]">
                        <span className="mt-0.5 shrink-0 text-zinc-500">•</span>
                        {txt}
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          </div>

          {/* Estratégia ProAdv */}
          <div className="rounded-lg bg-[var(--surface-muted)]/60 p-3">
            <p className="mb-1.5 text-xs font-semibold uppercase tracking-wide text-[var(--accent)]">
              Estratégia VoxisAdv
            </p>
            <p className="text-sm text-[var(--fg-muted)] leading-relaxed">{item.estrategia}</p>
          </div>

          {/* Fontes */}
          {item.fontes && item.fontes.length > 0 && (
            <div>
              <p className="mb-2 flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wide text-[var(--accent)]">
                <span>↗</span> Fontes e referências
              </p>
              <ul className="space-y-1.5">
                {item.fontes.map((fonte) => (
                  <li key={fonte.url} className="flex gap-2 text-sm">
                    <span className="mt-0.5 shrink-0 text-[var(--accent)]">•</span>
                    <a
                      href={fonte.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-[var(--accent)] hover:underline break-all"
                    >
                      {fonte.label}
                    </a>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </CardContent>
      )}
    </Card>
  );
}

export function SecaoSistemasProcessuais() {
  return (
    <section aria-label="Sistemas processuais por sistema">
      <div className="mb-6 space-y-2">
        <div className="flex items-center gap-2">
          <span className="rounded bg-[var(--surface-muted)] px-2 py-0.5 font-mono text-xs font-bold text-[var(--fg-muted)]">
            SEÇÃO 2
          </span>
          <h2 className="text-xl font-bold tracking-tight">Sistemas Processuais</h2>
        </div>
        <p className="max-w-2xl text-sm text-[var(--fg-muted)] leading-relaxed">
          Agrupado por sistema, não por tribunal. O eProc é o padrão nacional emergente — TJSP migrando desde out/2025, TJMG concluiu migração do PJe. Para SaaS privado, o único caminho viável é scraping autenticado (login/senha + 2FA é suficiente para consultar; certificado A1 é opcional). MNI/SOAP é restrito a órgãos do Judiciário.
        </p>
      </div>

      <div className="space-y-3">
        {sistemasProcessuais.map((item) => (
          <SistemaCard key={item.id} item={item} />
        ))}
      </div>
    </section>
  );
}
